import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/shared/hooks/useToast';
import { useCalendarConnections } from '@/features/calendar/public';
import { SettingsSectionHeader } from '../SettingsSectionHeader';
import { IntegrationsSkeleton } from './integrations/IntegrationsSkeleton';
import { IntegrationsEmptyState } from './integrations/IntegrationsEmptyState';
import { CalendarConnectionCard } from './integrations/CalendarConnectionCard';
import { ConnectProviderButtons } from './integrations/ConnectProviderButtons';

/**
 * Settings → Integrations. Connect/disconnect external calendar accounts and
 * configure linked calendars (scope, tenant, sync, color).
 *
 * OAuth callback lands on this same route with `?calendarConnection=<status>`
 * (`success` | `denied` | `error:<code>`). The toast is shown once and the
 * query param is removed immediately so a refresh does not re-fire it.
 */
export function IntegrationsSection() {
  const { t } = useTranslation('calendar');
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data: connections, isLoading, isError } = useCalendarConnections();

  useEffect(() => {
    const status = searchParams.get('calendarConnection');
    if (!status) return;

    if (status === 'success') {
      toast.success({ title: t('integrations.callback.success') });
    } else if (status === 'denied') {
      toast.info({ title: t('integrations.callback.denied') });
    } else {
      toast.error({ title: t('integrations.callback.error') });
    }

    navigate({ search: '' }, { replace: true });
    // Only on mount — searchParams identity changes after navigate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <IntegrationsSkeleton />;

  if (isError) {
    return (
      <div data-testid="settings-integrations-error" className="py-4 text-sm text-destructive">
        {t('integrations.loadError')}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-integrations-section">
      <div>
        <div className="pb-6">
          <SettingsSectionHeader>{t('integrations.title')}</SettingsSectionHeader>
          <p className="mt-1 text-sm text-muted-foreground">{t('integrations.description')}</p>
        </div>

        {connections.length === 0 ? (
          <IntegrationsEmptyState />
        ) : (
          <div className="space-y-4">
            {connections.map((connection) => (
              <CalendarConnectionCard key={connection.id} connection={connection} />
            ))}
            <ConnectProviderButtons />
          </div>
        )}
      </div>
    </div>
  );
}
